import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GridApi } from 'ag-grid-community';

import BoardContext from 'contexts/BoardContext';
import BulkGridActionsProvider from 'contextProviders/BulkGridActionsProvider';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import useBoardOpenedTracking from 'hooks/useBoardTracking';
import useContentViewTracking from 'hooks/useContentViewTracking';
import useReasonCodes from 'hooks/useReasonCodes';
import FlexCentered from 'components/Common/FlexCentered';
import Loading from 'components/Loading';
import MetricsGate from 'components/MetricsGate';

import SlideOutProvider from '../providers/SlideOutProvider';
import SignOffBoardContext from '../contexts/SignOffBoardContext';
import SelectedEntityProvider, {
  ENTITY_VALUE_QUERY_PARAM,
  PERIOD_QUERY_PARAM,
} from '../providers/SelectedEntityProvider';
import useEntityField from '../../TargetManager/hooks/useEntityField';
import useFilteringState from '../hooks/useFilteringState';
import PerformanceBoard from './PerformanceBoard';
import useRefreshDatasetFreshnessOnMount from '../../../hooks/useRefreshDatasetFreshnessOnMount';
import useDriverRewardOutputWakeupCall from './useDriverRewardOutputWakeupCall';
import getIdentifier from '../../../getIdentifier';

const PerformanceBoardContainer = ({
  board,
  popupFilters,
  scopeFilters,
  isBonusPeriodDriven,
  isPeriodSelectorForced,
  isSignOffBoard,
}: {
  board: PerformanceBoardTypes.Board;
  popupFilters?: FilterPlateType[];
  scopeFilters?: FilterPlateType[];
  isBonusPeriodDriven?: boolean;
  isPeriodSelectorForced?: boolean;
  isSignOffBoard?: boolean;
}) => {
  const [key, setKey] = useState<string>(getIdentifier());
  const [searchParams, setSearchParams] = useSearchParams();
  useContentViewTracking({
    type: 'board',
    name: board.name,
    typeId: board.id,
  });
  useRefreshDatasetFreshnessOnMount();
  useDriverRewardOutputWakeupCall(board);

  const entityField = useEntityField(board.dataType);
  const [sortOverride, setSortOverride] = useState<SimpleGridSort>(() =>
    board.defaultSort
      ? {
          field: board.defaultSort.field,
          direction: board.defaultSort.direction,
        }
      : { field: entityField || '', direction: 'asc' as 'asc' },
  );
  const [gridApi, setGridApi] = useState<GridApi>();
  const reasonCodes = useReasonCodes(board.dataType);

  useEffect(() => {
    if (board.defaultSort) {
      setSortOverride(board.defaultSort);
    }
  }, [board.defaultSort]);

  const onSlideOutClosed = useCallback(() => {
    if (searchParams.size === 0) {
      return;
    }

    searchParams.delete(ENTITY_VALUE_QUERY_PARAM);
    searchParams.delete(PERIOD_QUERY_PARAM);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const { onManualFilterChanged, onQuickFilterChanged } =
    useBoardOpenedTracking({ board, onSlideOutClosed });

  const {
    dataTypes,
    dateRange,
    quickFilters,
    drillDowns,
    setDrillDowns,
    scope,
    selectedQuickFilters,
    setSelectedQuickFilters,
    netQuickFilters,
  } = useFilteringState({
    board,
    popupFilters,
    scopeFilters,
  });

  const refreshBoard = useCallback(() => {
    setKey(getIdentifier());
  }, []);

  if (!dateRange) {
    return (
      <FlexCentered>
        <Loading />
      </FlexCentered>
    );
  }

  return (
    <SignOffBoardContext.Provider value={{ isSignOffBoard: !!isSignOffBoard }}>
      <MetricsGate content={board} contentType="Board">
        <FilterPlatesProvider
          drillDowns={drillDowns}
          setDrillDowns={setDrillDowns}
          scope={scope}
          dataTypes={dataTypes}
          quickFilters={netQuickFilters}
        >
          <BoardContext.Provider
            value={{
              board,
              reasonCodes,
              quickFilters,
              selectedQuickFilters,
              setSelectedQuickFilters,
              isBoardOwner: false,
            }}
          >
            <BulkGridActionsProvider>
              <GqlClientProvider
                visId={board.id}
                visType={'performanceBoard'}
                visName={board.name}
              >
                <SlideOutProvider
                  board={board}
                  refreshBoard={refreshBoard}
                  gridApi={gridApi}
                  key={key}
                >
                  <SelectedEntityProvider
                    board={board}
                    gridApi={gridApi}
                    key={key}
                  >
                    <PerformanceBoard
                      key={key}
                      board={board}
                      sortOverride={sortOverride}
                      onQuickFilterChanged={onQuickFilterChanged}
                      onManualFilterChanged={onManualFilterChanged}
                      dataTypes={dataTypes}
                      gridApi={gridApi}
                      setGridApi={setGridApi}
                      isBonusPeriodDriven={isBonusPeriodDriven}
                      isPeriodSelectorForced={isPeriodSelectorForced}
                    />
                  </SelectedEntityProvider>
                </SlideOutProvider>
              </GqlClientProvider>
            </BulkGridActionsProvider>
          </BoardContext.Provider>
        </FilterPlatesProvider>
      </MetricsGate>
    </SignOffBoardContext.Provider>
  );
};

export default PerformanceBoardContainer;
